/**
 * Suite connection-repo client. List is public-safe; creds are a second call.
 */

import { forgeFetch, type ForgeClientOptions } from "./client.js";
import type {
  Connection,
  ConnectionKind,
  ConnectionPurpose,
  ConnectionsListResponse,
  SelectConnectionResponse,
  SelectedByPurpose,
} from "./connections.js";

const POLL_MS = 5000;
let lastListAt = 0;
let listInflight: Promise<ConnectionsListResponse> | null = null;
let lastListKey = "";
let lastList: ConnectionsListResponse | null = null;

function optsKey(opts: ForgeClientOptions, extra: string): string {
  return `${opts.forgeApiUrl}|${extra}`;
}

export async function fetchSuiteConnections(
  opts: ForgeClientOptions,
  query?: {
    kind?: ConnectionKind | string;
    kinds?: string[];
    app?: string;
    force?: boolean;
  },
): Promise<ConnectionsListResponse> {
  const qs = new URLSearchParams();
  const kinds = (query?.kinds || []).map((k) => String(k || "").trim()).filter(Boolean);
  if (kinds.length) qs.set("kind", kinds.join(","));
  else if (query?.kind) qs.set("kind", String(query.kind));
  if (query?.app) qs.set("app", query.app);
  const path = `/api/suite/connections${qs.toString() ? `?${qs}` : ""}`;
  const key = optsKey(opts, path);
  const now = Date.now();
  if (
    !query?.force &&
    lastList &&
    lastListKey === key &&
    now - lastListAt < POLL_MS
  ) {
    return lastList;
  }
  if (!query?.force && listInflight && lastListKey === key) return listInflight;
  lastListKey = key;
  listInflight = (async () => {
    const data = await forgeFetch<ConnectionsListResponse>(opts, path);
    lastListAt = Date.now();
    lastList = data;
    return data;
  })().finally(() => {
    listInflight = null;
  });
  return listInflight;
}

export function fetchConnectionCreds(
  opts: ForgeClientOptions,
  id: string,
): Promise<{ ok?: boolean; modelId?: string | null; provider?: string | null; endpointUrl?: string | null }> {
  return forgeFetch(opts, `/api/suite/connections/${encodeURIComponent(id)}`);
}

export function selectSuiteConnection(
  opts: ForgeClientOptions,
  body: { appId: string; purpose: ConnectionPurpose; connectionId: string },
): Promise<SelectConnectionResponse> {
  lastList = null;
  return forgeFetch(opts, "/api/suite/connections/select", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function clearSuiteConnection(
  opts: ForgeClientOptions,
  body: { appId: string; purpose: ConnectionPurpose },
): Promise<{ ok: boolean }> {
  lastList = null;
  return forgeFetch(opts, "/api/suite/connections/clear", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function wakeForgeEndpoint(
  opts: ForgeClientOptions,
  sourceId: string,
): Promise<unknown> {
  lastList = null;
  return forgeFetch(opts, `/api/endpoints/${encodeURIComponent(sourceId)}/wake`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

/** Dedicated Music 3 scale-from-zero kick — no GPU-origin JWT. */
export function wakeMusic3Endpoint(
  opts: ForgeClientOptions,
  body?: { endpoint?: string; name?: string },
): Promise<unknown> {
  lastList = null;
  return forgeFetch(opts, "/api/tts/music3/wake", {
    method: "POST",
    body: JSON.stringify(body || {}),
  });
}

export function fetchMusic3Wake(
  opts: ForgeClientOptions,
): Promise<{ phase?: string; code?: string }> {
  return forgeFetch(opts, "/api/tts/music3/wake", { method: "GET" });
}

export function sleepForgeEndpoint(
  opts: ForgeClientOptions,
  sourceId: string,
): Promise<unknown> {
  lastList = null;
  return forgeFetch(opts, `/api/endpoints/${encodeURIComponent(sourceId)}/sleep`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export type { Connection, SelectedByPurpose };
